import { Box, Button, IconButton, paletteColors, Popover, Typography } from '@evaneos/b2b-ui';
import CalendarMonthOutlinedIcon from '@evaneos/b2b-ui/icons/CalendarMonthOutlined';
import CloseIcon from '@evaneos/b2b-ui/icons/Close';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { Appointment } from '../../../../../resources/appointment/types';

import { AppointmentCancelConfirmDialog } from './AppointmentCancelConfirmDialog';
import {
    formatAppointmentButtonParts,
    formatAppointmentPopoverDate,
    formatAppointmentTimeRange,
} from './appointmentBlock.operations';

type Props = {
    appointments?: Array<Appointment>;
    isProviderConnected: boolean;
    onCancelAppointment?: () => void;
};

export function AppointmentBlock({
    appointments,
    isProviderConnected,
    onCancelAppointment,
}: Readonly<Props>) {
    const { formatMessage } = useIntl();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

    const isOpen = Boolean(anchorEl);

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleCancelClick = () => {
        handleClose();
        setIsCancelDialogOpen(true);
    };

    const handleCancelDialogClose = () => {
        setIsCancelDialogOpen(false);
    };

    const handleCancelConfirm = () => {
        onCancelAppointment?.();
        setIsCancelDialogOpen(false);
    };

    if (!appointments || appointments.length < 1) {
        if (!isProviderConnected) {
            return null;
        }

        return (
            <Button
                variant="outlined"
                startIcon={<CalendarMonthOutlinedIcon />}
                disabled
                data-spec-id="appointment-block"
            >
                {formatMessage({ id: 'appointment.block.no_rdv' })}
            </Button>
        );
    }

    const [appointment] = appointments;
    const { date, time } = formatAppointmentButtonParts(appointment.startDateTime);

    return (
        <>
            <Button
                variant="outlined"
                startIcon={<CalendarMonthOutlinedIcon />}
                onClick={handleOpen}
                data-spec-id="appointment-block"
                aria-describedby={isOpen ? 'appointment-popover' : undefined}
                sx={{
                    backgroundColor: paletteColors.blue[100],
                    color: paletteColors.blue[900],
                    borderColor: paletteColors.blue[900],
                    '&:hover': {
                        backgroundColor: paletteColors.blue[200],
                        color: paletteColors.blue[800],
                        borderColor: paletteColors.blue[800],
                    },
                }}
            >
                {formatMessage({ id: 'appointment.block.button_label' }, { date, time })}
            </Button>
            <Popover
                id="appointment-popover"
                open={isOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { p: 2, width: 282 } } }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                    <Typography variant="subtitle1" fontWeight="bold">
                        {formatAppointmentPopoverDate(appointment.startDateTime)}
                    </Typography>
                    <IconButton size="small" onClick={handleClose} sx={{ mt: -0.5, mr: -0.5 }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
                <Typography variant="body2">
                    {formatAppointmentTimeRange(appointment.startDateTime, appointment.endDateTime)}
                </Typography>
                <Box mt={1.5}>
                    <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        data-spec-id="appointment-cancel-button"
                        onClick={handleCancelClick}
                    >
                        {formatMessage({ id: 'appointment.block.cancel_button' })}
                    </Button>
                </Box>
            </Popover>
            <AppointmentCancelConfirmDialog
                isOpen={isCancelDialogOpen}
                onClose={handleCancelDialogClose}
                onConfirm={handleCancelConfirm}
            />
        </>
    );
}
